import { SaveService } from '@core/services/SaveService';
import type { PlayerProfileData } from '@core/types';

const DEFAULT_PROFILE: PlayerProfileData = {
  name: '',
  coins: 0,
  highScores: {},
  unlocked: [],
  character: '',
  ownedItems: [],
  accessory: '',
};

/**
 * Fuente unica de verdad del estado del jugador. Cualquier escena o modulo lee y
 * escribe el progreso a traves de esta clase, que se encarga de persistirlo.
 * Se inyecta a las escenas mediante el registry global de Phaser.
 */
export class PlayerProfile {
  private data: PlayerProfileData;

  public constructor(private readonly saveService: SaveService) {
    const loaded = saveService.load();
    // Mezclamos con los valores por defecto para que perfiles guardados con versiones
    // anteriores reciban los campos nuevos (ej. character) sin romperse.
    this.data = loaded
      ? { ...structuredClone(DEFAULT_PROFILE), ...loaded }
      : structuredClone(DEFAULT_PROFILE);
  }

  public get name(): string {
    return this.data.name;
  }

  public get coins(): number {
    return this.data.coins;
  }

  public setName(name: string): void {
    this.data.name = name.trim();
    this.persist();
  }

  public get character(): string {
    return this.data.character;
  }

  public setCharacter(characterKey: string): void {
    this.data.character = characterKey;
    this.persist();
  }

  public addCoins(amount: number): void {
    if (amount <= 0) {
      return;
    }
    this.data.coins += amount;
    this.persist();
  }

  public getHighScore(minigameKey: string): number {
    return this.data.highScores[minigameKey] ?? 0;
  }

  /** Registra un puntaje y devuelve true si supero el record anterior. */
  public submitScore(minigameKey: string, score: number): boolean {
    const previous = this.getHighScore(minigameKey);
    if (score <= previous) {
      return false;
    }
    this.data.highScores[minigameKey] = score;
    this.persist();
    return true;
  }

  public get ownedItems(): readonly string[] {
    return this.data.ownedItems;
  }

  public ownsItem(itemId: string): boolean {
    return this.data.ownedItems.includes(itemId);
  }

  /** Intenta comprar un item: descuenta monedas si alcanza y no lo tenia. */
  public buyItem(itemId: string, price: number): boolean {
    if (this.ownsItem(itemId) || this.data.coins < price) {
      return false;
    }
    this.data.coins -= price;
    this.data.ownedItems.push(itemId);
    this.persist();
    return true;
  }

  public get accessory(): string {
    return this.data.accessory;
  }

  /** Equipa un accesorio (o lo quita si se pasa cadena vacia). */
  public setAccessory(itemId: string): void {
    this.data.accessory = itemId;
    this.persist();
  }

  public isUnlocked(minigameKey: string): boolean {
    return this.data.unlocked.includes(minigameKey);
  }

  public unlock(minigameKey: string): void {
    if (this.isUnlocked(minigameKey)) {
      return;
    }
    this.data.unlocked.push(minigameKey);
    this.persist();
  }

  private persist(): void {
    this.saveService.save(this.data);
  }
}
